import { RadioGroup, RadioGroupItem } from "@gigpro/ui/RadioGroup";
import { cn } from "@gigpro/ui/tailwind/cn";
import { type PayPerHireChoiceCopy } from "domains/hire/jobsPlan";
import type React from "react";

export type PayPerHireOption = "fee" | "switch";

interface PayPerHireChoiceProps {
  copy: PayPerHireChoiceCopy;
  /** Only a location admin can move the location to the Jobs plan; everyone else just sees the fee. */
  canSwitch: boolean;
  value: PayPerHireOption;
  onValueChange: (value: PayPerHireOption) => void;
  disabled?: boolean;
  /** Today's fee summary, shown under "Pay the hire fee". */
  feeSummary: React.ReactNode;
}

const optionClass = (selected: boolean) =>
  cn("flex flex-col gap-2 rounded-sm border p-2", selected ? "border-brand" : "border-light-gray");

/** Hire dialog for a pay-per-hire location: pay the fee as today, or switch to the Jobs plan and hire for no fee. */
export const PayPerHireChoice: React.FC<PayPerHireChoiceProps> = ({
  copy,
  canSwitch,
  value,
  onValueChange,
  disabled,
  feeSummary,
}) => {
  if (!canSwitch) {
    return (
      <>
        {feeSummary}
        <p className="text-normal text-black">{copy.askAdmin}</p>
      </>
    );
  }
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next as PayPerHireOption)}
      disabled={disabled}
      aria-label="How to pay for this hire"
    >
      <div className={optionClass(value === "fee")}>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="fee" id="pay-per-hire-fee" />
          <label htmlFor="pay-per-hire-fee" className="text-normal font-semibold text-black">
            Pay the hire fee
          </label>
        </div>
        {feeSummary}
      </div>
      <div className={optionClass(value === "switch")}>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="switch" id="pay-per-hire-switch" />
          <label htmlFor="pay-per-hire-switch" className="text-normal font-semibold text-black">
            {copy.label}
          </label>
        </div>
        <p className="text-normal text-black">{copy.body}</p>
        <p className="text-normal text-black">{copy.oneWayDoor}</p>
      </div>
    </RadioGroup>
  );
};
